import React, { useState } from 'react';
import {
  Zap, Search, Filter, Plus, Edit, Trash2, Eye, CheckCircle,
  XCircle, AlertCircle, Database, Link as LinkIcon, Settings
} from 'lucide-react';

const PostingRulesTab = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');

  const postingRules = [
    {
      id: 'RULE-001',
      name: 'Goods Receipt - Inventory',
      eventType: 'GOODS_RECEIPT',
      sourceModule: 'WMS',
      description: 'When goods are received in WMS',
      enabled: true,
      rules: [
        {
          account: '1130 - Inventory',
          type: 'Debit',
          formula: 'qty * unit_cost',
          condition: null
        },
        {
          account: '2130 - GR/IR Clearing',
          type: 'Credit',
          formula: 'qty * unit_cost',
          condition: null
        }
      ],
      lastUsed: '2024-02-11 10:30:00',
      usageCount: 1250
    },
    {
      id: 'RULE-002',
      name: 'Vendor Invoice Match',
      eventType: 'VENDOR_INVOICE_MATCH',
      sourceModule: 'Procurement',
      description: 'When vendor invoice is matched',
      enabled: true,
      rules: [
        {
          account: '2130 - GR/IR Clearing',
          type: 'Debit',
          formula: 'invoice_amount',
          condition: null
        },
        {
          account: '2110 - Accounts Payable',
          type: 'Credit',
          formula: 'invoice_amount',
          condition: null
        }
      ],
      lastUsed: '2024-02-11 09:15:00',
      usageCount: 890
    },
    {
      id: 'RULE-003',
      name: 'Goods Shipment - COGS',
      eventType: 'GOODS_SHIPMENT',
      sourceModule: 'WMS',
      description: 'When goods are shipped',
      enabled: true,
      rules: [
        {
          account: '5100 - Cost of Goods Sold',
          type: 'Debit',
          formula: 'qty * wac_cost',
          condition: null
        },
        {
          account: '1130 - Inventory',
          type: 'Credit',
          formula: 'qty * wac_cost',
          condition: null
        }
      ],
      lastUsed: '2024-02-11 11:45:00',
      usageCount: 2100
    },
    {
      id: 'RULE-004',
      name: 'Sales Invoice',
      eventType: 'SALES_INVOICE',
      sourceModule: 'Order Management',
      description: 'When sales invoice is generated',
      enabled: true,
      rules: [
        {
          account: '1120 - Accounts Receivable',
          type: 'Debit',
          formula: 'total_amount',
          condition: null
        },
        {
          account: '4100 - Sales Revenue',
          type: 'Credit',
          formula: 'subtotal_amount',
          condition: null
        },
        {
          account: '2115 - VAT Payable',
          type: 'Credit',
          formula: 'vat_amount',
          condition: null
        }
      ],
      lastUsed: '2024-02-11 12:00:00',
      usageCount: 1850
    },
    {
      id: 'RULE-005',
      name: 'Asset Capitalization',
      eventType: 'ASSET_CAPITALIZATION',
      sourceModule: 'Assets',
      description: 'When asset is capitalized',
      enabled: true,
      rules: [
        {
          account: '1210 - Fixed Assets',
          type: 'Debit',
          formula: 'purchase_cost',
          condition: null
        },
        {
          account: '2110 - Accounts Payable',
          type: 'Credit',
          formula: 'purchase_cost',
          condition: 'payment_method == "credit"'
        },
        {
          account: '1110 - Cash',
          type: 'Credit',
          formula: 'purchase_cost',
          condition: 'payment_method == "cash"'
        }
      ],
      lastUsed: '2024-02-10 14:20:00',
      usageCount: 45
    },
    {
      id: 'RULE-006',
      name: 'Monthly Depreciation',
      eventType: 'DEPRECIATION_RUN',
      sourceModule: 'Assets',
      description: 'Automated monthly depreciation',
      enabled: true,
      rules: [
        {
          account: '5300 - Depreciation Expense',
          type: 'Debit',
          formula: 'monthly_depreciation',
          condition: null
        },
        {
          account: '1220 - Accumulated Depreciation',
          type: 'Credit',
          formula: 'monthly_depreciation',
          condition: null
        }
      ],
      lastUsed: '2024-02-01 00:00:00',
      usageCount: 12
    },
    {
      id: 'RULE-007',
      name: 'Stock Adjustment',
      eventType: 'STOCK_ADJUSTMENT',
      sourceModule: 'WMS',
      description: 'When inventory is adjusted',
      enabled: true,
      rules: [
        {
          account: '1130 - Inventory',
          type: 'Debit',
          formula: 'adjustment_amount',
          condition: 'adjustment_type == "increase"'
        },
        {
          account: '1130 - Inventory',
          type: 'Credit',
          formula: 'adjustment_amount',
          condition: 'adjustment_type == "decrease"'
        },
        {
          account: '5105 - Inventory Variance',
          type: 'Debit',
          formula: 'adjustment_amount',
          condition: 'adjustment_type == "decrease"'
        },
        {
          account: '5105 - Inventory Variance',
          type: 'Credit',
          formula: 'adjustment_amount',
          condition: 'adjustment_type == "increase"'
        }
      ],
      lastUsed: '2024-02-11 08:30:00',
      usageCount: 320
    }
  ];

  const eventTypes = [
    'GOODS_RECEIPT',
    'VENDOR_INVOICE_MATCH',
    'GOODS_SHIPMENT',
    'SALES_INVOICE',
    'ASSET_CAPITALIZATION',
    'DEPRECIATION_RUN',
    'STOCK_ADJUSTMENT',
    'PAYMENT_RECEIVED',
    'PAYMENT_MADE'
  ];

  const filteredRules = postingRules.filter(rule => {
    if (filter === 'all') return true;
    if (filter === 'enabled') return rule.enabled;
    if (filter === 'disabled') return !rule.enabled;
    return rule.sourceModule === filter;
  });

  return (
    <div className="space-y-6">
      {/* Header Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <Database className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-900 mb-1">Posting Rules Engine</h3>
            <p className="text-sm text-blue-700">
              Configure automatic posting rules that translate business events from operational modules into journal entries.
              Rules are executed in real-time when events are received via the API endpoint.
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search rules..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium bg-white"
          >
            <option value="all">All Rules</option>
            <option value="enabled">Enabled</option>
            <option value="disabled">Disabled</option>
            <option value="WMS">WMS</option>
            <option value="Procurement">Procurement</option>
            <option value="Order Management">Order Management</option>
            <option value="Assets">Assets</option>
          </select>
        </div>
        <div className="flex items-center gap-3">
          <button className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Settings
          </button>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2">
            <Plus className="h-4 w-4" />
            New Rule
          </button>
        </div>
      </div>

      {/* Rules List */}
      <div className="space-y-4">
        {filteredRules.map((rule) => (
          <div key={rule.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-5">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{rule.name}</h3>
                    {rule.enabled ? (
                      <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-700 flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" />
                        Enabled
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-700 flex items-center gap-1">
                        <XCircle className="h-3 w-3" />
                        Disabled
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{rule.description}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <LinkIcon className="h-3 w-3" />
                      {rule.sourceModule}
                    </span>
                    <span className="flex items-center gap-1">
                      <Zap className="h-3 w-3" />
                      {rule.eventType}
                    </span>
                    <span>Used {rule.usageCount} times</span>
                    <span>Last: {rule.lastUsed}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button className="p-2 hover:bg-gray-100 rounded-lg">
                    <Eye className="h-4 w-4 text-gray-600" />
                  </button>
                  <button className="p-2 hover:bg-gray-100 rounded-lg">
                    <Edit className="h-4 w-4 text-gray-600" />
                  </button>
                  <button className="p-2 hover:bg-red-100 rounded-lg">
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </button>
                </div>
              </div>

              {/* Posting Rules */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Posting Rules:</h4>
                <div className="space-y-2">
                  {rule.rules.map((postingRule, index) => (
                    <div key={index} className="flex items-center gap-4 text-sm bg-white rounded p-3 border border-gray-200">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        postingRule.type === 'Debit' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {postingRule.type}
                      </span>
                      <span className="font-medium text-gray-900 flex-1">{postingRule.account}</span>
                      <code className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                        {postingRule.formula}
                      </code>
                      {postingRule.condition && (
                        <span className="text-xs text-gray-500 italic">
                          if: {postingRule.condition}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* API Endpoint Info */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 mb-2">API Endpoint</h3>
        <p className="text-sm text-gray-600 mb-3">
          External modules can post events to this endpoint to trigger automatic journal entries:
        </p>
        <div className="bg-white rounded-lg p-4 border border-gray-300">
          <code className="text-sm font-mono text-gray-900">
            POST /api/v1/financial/events
          </code>
          <pre className="mt-3 text-xs text-gray-600 bg-gray-50 p-3 rounded overflow-x-auto">
{`{
  "event_type": "GOODS_RECEIPT",
  "source_module": "WMS",
  "data": {
    "item_id": "SKU-101",
    "qty": 10,
    "unit_cost": 50.00,
    "vendor_id": "V-500",
    "po_number": "PO-2024-001"
  }
}`}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default PostingRulesTab;
